import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';

const ACTIVITY_COLORS = {
    running: '#FC4C02',
    cycling: '#2D86C8',
    hiking: '#22A844',
    swimming: '#0AAFDB',
    other: '#888888',
};

const RoutePreview = ({ routePolyline, activityType }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);

    // Initialize map and draw route whenever the polyline changes
    useEffect(() => {
        const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
        if (!token || !routePolyline || !containerRef.current) return;

        let coordinates = [];
        try {
            coordinates = polyline.decode(routePolyline).map(([lat, lng]) => [lng, lat]);
        } catch (_) {
            return;
        }
        if (coordinates.length < 2) return;

        mapboxgl.accessToken = token;
        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/dark-v11',
            interactive: false,
            attributionControl: false,
        });
        mapRef.current = map;

        map.on('load', () => {
            map.addSource('route', {
                type: 'geojson',
                data: { type: 'Feature', geometry: { type: 'LineString', coordinates } },
            });

            map.addLayer({
                id: 'route-line',
                type: 'line',
                source: 'route',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': ACTIVITY_COLORS[activityType] || ACTIVITY_COLORS.other,
                    'line-width': 3,
                    'line-opacity': 0.9,
                },
            });

            const bounds = coordinates.reduce(
                (b, coord) => b.extend(coord),
                new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
            );
            map.fitBounds(bounds, { padding: 24, duration: 0 });
        });

        return () => {
            map.remove();
            mapRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routePolyline]);

    // Update line color when activityType changes without reinitializing the map
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) return;
        if (!map.getLayer('route-line')) return;
        map.setPaintProperty('route-line', 'line-color', ACTIVITY_COLORS[activityType] || ACTIVITY_COLORS.other);
    }, [activityType]);

    if (!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || !routePolyline) return null;

    return (
        <div style={{ borderRadius: '8px', overflow: 'hidden', height: '200px', marginTop: '12px' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        </div>
    );
};

export default RoutePreview;
